import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './RedefinirSenha.css';
import { getApiBaseUrl } from './utils/apiConfig';
import { ArrowLeft, Lock } from 'lucide-react';
import ProtectedBrandLogo from './components/ProtectedBrandLogo';

const API_BASE_URL = getApiBaseUrl();

const RedefinirSenha = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const tokenFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return params.get('token') || '';
  }, [location.search]);

  const token = tokenFromUrl;
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    document.body.classList.add('login-novo-active');
    return () => document.body.classList.remove('login-novo-active');
  }, []);

  const canSubmit = useMemo(() => {
    if (!token) return false;
    if (!newPassword || !confirmPassword) return false;
    if (newPassword !== confirmPassword) return false;
    return true;
  }, [token, newPassword, confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/forgot-password-reset`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword, confirmPassword })
      });

      const data = await res.json().catch(() => null);
      if (res.ok && data?.success) {
        setSuccess(true);
      } else {
        setError(data?.message || 'Nao foi possivel redefinir sua senha. Tente novamente.');
      }
    } catch (_) {
      setError('Nao foi possivel redefinir sua senha. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-novo-page redefinir-senha-page">
      <div className="login-novo-glow" aria-hidden />
      <div className="login-novo-shell">
        <div className="login-novo-brand">
          <ProtectedBrandLogo />
        </div>

        <div className="login-novo-card redefinir-senha-card">
          <div className="login-novo-header">
            <h1>Redefinir senha</h1>
            <p>{success ? '' : 'Defina sua nova senha com seguranca.'}</p>
          </div>

          {success ? (
            <div className="esqueceu-senha-success">
              <p className="esqueceu-senha-success-message">Senha alterada com sucesso. Voce ja pode fazer login.</p>
              <button
                type="button"
                className="login-novo-forgot-link esqueceu-senha-back"
                onClick={() => navigate('/')}
              >
                <ArrowLeft size={14} />
                Voltar ao login
              </button>
            </div>
          ) : (
            <form className="login-novo-form" onSubmit={handleSubmit}>
              {!token ? (
                <div className="login-novo-error" role="alert">
                  <i className="bi bi-exclamation-circle" />
                  <span>Link invalido ou expirado.</span>
                </div>
              ) : (
                <>
                  <div className="login-novo-field">
                    <label htmlFor="redefinir-senha-nova">Nova senha</label>
                    <div className="login-novo-input-wrapper">
                      <Lock size={15} className="login-novo-input-icon" />
                      <input
                        type="password"
                        id="redefinir-senha-nova"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Digite sua nova senha"
                        className="login-novo-input"
                        autoComplete="new-password"
                        autoFocus
                      />
                    </div>
                  </div>

                  <div className="login-novo-field">
                    <label htmlFor="redefinir-senha-confirmar">Confirmar senha</label>
                    <div className="login-novo-input-wrapper">
                      <Lock size={15} className="login-novo-input-icon" />
                      <input
                        type="password"
                        id="redefinir-senha-confirmar"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirme sua nova senha"
                        className="login-novo-input"
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                </>
              )}

              {error && (
                <div className="login-novo-error" role="alert">
                  <i className="bi bi-exclamation-circle" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                className="login-novo-submit"
                disabled={!canSubmit || submitting || !token}
              >
                {submitting ? (
                  <>
                    <i className="bi bi-arrow-repeat login-novo-spinner" />
                    Aplicando...
                  </>
                ) : (
                  'Aplicar nova senha'
                )}
              </button>

              <button
                type="button"
                className="login-novo-forgot-link esqueceu-senha-back"
                onClick={() => navigate('/')}
              >
                <ArrowLeft size={14} />
                Voltar ao login
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default RedefinirSenha;

